import { api } from '@/shared/api/client'

/**
 * Axis Advisory — the client for `/api/v1/advisor`.
 *
 * ## Three things the shapes here are trying to make obvious
 *
 * **A run is the unit.** Every answer in this module — a playbook report, a
 * conversational turn, one unit of a map fan-out — is a `Run` with its own
 * brief, steps, cost and report. There is no lighter path, so there is no
 * lighter type.
 *
 * **The steps are the progress bar.** The only network door in the backend
 * returns a byte array, not a stream, so there is nothing to render token by
 * token. Instead `RunResponse.steps` fills in as models answer, and the console
 * draws a board from it — which survives a reload and shows four models working
 * at once, neither of which a cursor does.
 *
 * **A visual carries its own data.** `resolved.points` is read out of the brief
 * by the server; the model only chose the chart and named a dataset. A visual
 * with `bindingStatus: 'UNBOUND_DROPPED'` is one the model asked for that did
 * not resolve — it is returned rather than hidden, because the count of those is
 * the best early signal that a prompt has drifted.
 */

// ── Catalogue ───────────────────────────────────────────────────────────────

export interface Playbook {
  id: string
  code: string
  title: string
  summary: string
  icon: string | null
  category: string
  councilCode: string
  requiredAuthority: string
  sensitivity: 'STANDARD' | 'SENSITIVE'
  defaultScopeKind: string
  contributors: string[]
  enabled: boolean
  systemDefined: boolean
  rank: number
  systemPrompt: string
}

export interface CouncilStage {
  key: string
  kind: string
  label: string
  capabilities: string[]
  tier: string | null
  vendorDiverseFrom: string[]
  dependsOn: string[]
  fanOut: string | null
  maxTokens: number
  effort: string | null
  /** What the router WOULD pick right now — null when nothing matches. */
  resolvedModel: string | null
  resolvedVendor: string | null
  routingNote: string | null
}

export interface Council {
  id: string
  code: string
  title: string
  description: string | null
  enabled: boolean
  systemDefined: boolean
  stages: CouncilStage[]
}

export interface Model {
  id: string
  vendor: string
  modelKey: string
  displayName: string
  integrationCode: string | null
  wire: 'ANTHROPIC' | 'OPENAI_CHAT' | 'GEMINI'
  capabilities: string[]
  tier: string
  contextTokens: number
  maxOutputTokens: number
  inputCostPerMtok: number
  outputCostPerMtok: number
  supportsSampling: boolean
  supportsTools: boolean
  supportsJsonSchema: boolean
  supportsSystem: boolean
  defaultEffort: string | null
  rank: number
  status: string
  catalogueStatus: 'UNVERIFIED' | 'VERIFIED' | 'MISSING_AT_PROVIDER'
  lastVerifiedAt: string | null
  /** Whether an ACTIVE integration with a credential actually stands behind it. */
  reachable: boolean
  notes: string | null
}

// ── Runs ────────────────────────────────────────────────────────────────────

export interface RunStep {
  id: string
  stageKey: string
  stageKind: string
  ordinal: number
  label: string
  vendor: string | null
  modelKey: string | null
  status: string
  inputTokens: number
  outputTokens: number
  latencyMs: number | null
  costUsd: number
  errorCode: string | null
  errorMessage: string | null
  startedAt: string | null
  finishedAt: string | null
}

export interface Run {
  id: string
  playbookCode: string
  councilCode: string
  title: string
  triggerKind: string
  sensitivity: string
  scopeKind: string
  scopeRef: string | null
  scopeLabel: string
  question: string | null
  status: string
  briefId: string | null
  reportId: string | null
  threadId: string | null
  parentRunId: string | null
  requestedAt: string
  startedAt: string | null
  finishedAt: string | null
  inputTokens: number
  outputTokens: number
  costUsd: number
  budgetCapUsd: number
  errorCode: string | null
  errorMessage: string | null
  steps: RunStep[]
  childCount: number
}

export interface Brief {
  id: string
  runId: string | null
  scopeLabel: string
  sections: Record<string, unknown>[]
  datasets: Record<string, unknown>
  markdown: string
  contentHash: string
  tokenEstimate: number
  redactions: Record<string, unknown>[]
  authoritySnapshot: string[]
  builtAt: string
}

// ── Reports ─────────────────────────────────────────────────────────────────

export interface Finding {
  id: string
  ordinal: number
  kind: string
  severity: 'INFO' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  title: string
  detail: string
  /** Required by the database. A finding without a consequence never gets stored. */
  soWhat: string
  confidence: number | null
  evidence: { datasetId?: string; rows?: number[]; note?: string }[]
  geoUnitIds: string[]
  geoMetric: number | null
  geoMetricLabel: string | null
  contested: boolean
  contestedNote: string | null
}

export interface VisualPoint {
  label: string
  unitId?: string
  values: (number | null)[]
}

export interface Visual {
  id: string
  ordinal: number
  kind: string
  title: string
  subtitle: string | null
  caption: string | null
  spec: Record<string, unknown>
  resolved: {
    datasetId?: string
    seriesNames?: string[]
    unit?: string
    valueFormat?: string
    points?: VisualPoint[]
    sourceTitle?: string
    truncated?: boolean
  } | null
  bindingStatus: 'BOUND' | 'UNBOUND_DROPPED'
  bindingError: string | null
}

export interface Proposal {
  id: string
  runId: string
  reportId: string | null
  findingId: string | null
  kind: string
  title: string
  rationale: string
  payload: Record<string, unknown>
  dryRun: Record<string, unknown> | null
  expectedEffect: string | null
  riskNote: string | null
  status: string
  requiredAuthority: string
  deepLink: string | null
  /** The second authority check, answered in advance so the button can say why. */
  callerMayApply: boolean
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  appliedAt: string | null
  applyResult: Record<string, unknown> | null
  expiresAt: string | null
  createdAt: string
}

export interface Report {
  id: string
  runId: string
  title: string
  headline: string
  executiveSummary: string
  bodyMarkdown: string | null
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  confidenceRationale: string
  disagreement: string | null
  generatedByModel: string | null
  verifiedByModel: string | null
  status: string
  pinned: boolean
  createdAt: string
  findings: Finding[]
  visuals: Visual[]
  proposals: Proposal[]
  run: Run | null
}

// ── Analyst ─────────────────────────────────────────────────────────────────

export interface Thread {
  id: string
  title: string
  scopeKind: string
  scopeRef: string | null
  scopeLabel: string | null
  pinned: boolean
  archived: boolean
  messageCount: number
  lastMessageAt: string | null
  forkedFromThreadId: string | null
  createdAt: string
}

export interface Message {
  id: string
  ordinal: number
  role: 'USER' | 'ASSISTANT' | 'SYSTEM'
  content: string
  runId: string | null
  runStatus: string | null
  modelKey: string | null
  inputTokens: number
  outputTokens: number
  retryOfId: string | null
  /** Set on a turn a retry improved on. Collapsed in the UI, never deleted. */
  superseded: boolean
  retryNote: string | null
  reportId: string | null
  createdAt: string
}

export interface ThreadDetail {
  thread: Thread
  messages: Message[]
}

// ── Governance ──────────────────────────────────────────────────────────────

export interface Asset {
  id: string
  runId: string | null
  proposalId: string | null
  kind: 'IMAGE' | 'VIDEO'
  prompt: string
  vendor: string | null
  modelKey: string | null
  aspectRatio: string | null
  durationSeconds: number | null
  status: string
  synthetic: boolean
  disclosureText: string
  errorMessage: string | null
  storageFileId: string | null
  createdAt: string
}

export interface Budget {
  periodMonth: string
  capUsd: number
  spentUsd: number
  hardStop: boolean
  alertAtPct: number
  alertedAt: string | null
  usdToKes: number
}

export interface UsageRow {
  label: string
  runs: number
  inputTokens: number
  outputTokens: number
  costUsd: number
}

export interface Usage {
  budget: Budget
  byVendor: UsageRow[]
  byModel: UsageRow[]
  byPlaybook: UsageRow[]
  byDay: UsageRow[]
  totalRuns: number
  failedRuns: number
}

export interface Schedule {
  id: string
  playbookCode: string
  title: string
  cadence: string
  hourLocal: number
  dayOfWeek: number | null
  dayOfMonth: number | null
  scopeKind: string
  scopeRef: string | null
  scopeLabel: string | null
  question: string | null
  recipients: string[]
  pinReport: boolean
  enabled: boolean
  lastRunAt: string | null
  lastRunId: string | null
  nextRunAt: string | null
}

export interface Overview {
  pinnedBrief: Report | null
  playbooks: Playbook[]
  openProposals: number
  runsToday: number
  runningNow: number
  budget: Budget
  configuredModels: number
  reachableModels: number
  configuredVendors: string[]
  /**
   * Whether TWO vendors are reachable. When false, no report on this deployment
   * can claim HIGH confidence — the red team has nobody else to be.
   */
  vendorDiversityPossible: boolean
  mediaEnabled: boolean
  recentRuns: Run[]
}

export interface FanOutEstimate {
  unitCount: number
  capped: boolean
  cap: number
  estimatedCostUsd: number
  sampleUnits: string[]
}

// ── Calls ───────────────────────────────────────────────────────────────────

export const overview = () => api.get<Overview>('/advisor/overview')
export const listPlaybooks = () => api.get<Playbook[]>('/advisor/playbooks')
export const listCouncils = () => api.get<Council[]>('/advisor/councils')

export const startRun = (body: {
  playbookCode: string
  scopeKind?: string
  scopeRef?: string | null
  question?: string
  title?: string
}) => api.post<Run>('/advisor/runs', body)

export const listRuns = (page = 0, size = 25) =>
  api.get<Run[]>(`/advisor/runs?page=${page}&size=${size}`)
export const getRun = (id: string) => api.get<Run>(`/advisor/runs/${id}`)
export const runChildren = (id: string) => api.get<Run[]>(`/advisor/runs/${id}/children`)
export const cancelRun = (id: string) => api.post<Run>(`/advisor/runs/${id}/cancel`)
export const getBrief = (runId: string) => api.get<Brief>(`/advisor/runs/${runId}/brief`)
export const reportForRun = (runId: string) => api.get<Report>(`/advisor/runs/${runId}/report`)

export const fanOutEstimate = (parentUnitId: string, childUnitType: string) =>
  api.get<FanOutEstimate>(
    `/advisor/runs/fan-out/estimate?parentUnitId=${parentUnitId}&childUnitType=${childUnitType}`)
export const fanOut = (body: {
  playbookCode: string
  parentUnitId: string
  childUnitType: string
  question?: string
}) => api.post<Run>('/advisor/runs/fan-out', body)

export const listReports = (page = 0, size = 20) =>
  api.get<Report[]>(`/advisor/reports?page=${page}&size=${size}`)
export const getReport = (id: string) => api.get<Report>(`/advisor/reports/${id}`)
export const pinReport = (id: string, pinned: boolean) =>
  api.post<Report>(`/advisor/reports/${id}/pin`, { pinned })
export const setReportStatus = (id: string, status: string) =>
  api.post<Report>(`/advisor/reports/${id}/status`, { status })
export const listFindings = (page = 0, size = 60) =>
  api.get<Finding[]>(`/advisor/findings?page=${page}&size=${size}`)

export const listProposals = (status?: string, page = 0, size = 60) =>
  api.get<Proposal[]>(
    `/advisor/proposals?page=${page}&size=${size}${status ? `&status=${status}` : ''}`)
export const getProposal = (id: string) => api.get<Proposal>(`/advisor/proposals/${id}`)
export const refreshDryRun = (id: string) =>
  api.post<Proposal>(`/advisor/proposals/${id}/dry-run`)
export const decideProposal = (id: string, approve: boolean, note?: string) =>
  api.post<Proposal>(`/advisor/proposals/${id}/decision`, { approve, note })
export const applyProposal = (id: string) => api.post<Proposal>(`/advisor/proposals/${id}/apply`)

export const listThreads = (archived = false) =>
  api.get<Thread[]>(`/advisor/threads?archived=${archived}`)
export const createThread = (body: {
  title?: string
  scopeKind?: string
  scopeRef?: string | null
  scopeLabel?: string
}) => api.post<Thread>('/advisor/threads', body)
export const getThread = (id: string) => api.get<ThreadDetail>(`/advisor/threads/${id}`)
export const ask = (id: string, body: {
  question: string
  playbookCode?: string
  retryOf?: string
  retryNote?: string
}) => api.post<Message>(`/advisor/threads/${id}/ask`, body)
export const forkThread = (id: string, fromMessageId: string, title?: string) =>
  api.post<Thread>(`/advisor/threads/${id}/fork`, { fromMessageId, title })
export const updateThread = (id: string, body: {
  title?: string
  pinned?: boolean
  archived?: boolean
}) => api.patch<Thread>(`/advisor/threads/${id}`, body)

export const listModels = () => api.get<Model[]>('/advisor/models')
export const modelCapabilities = () => api.get<string[]>('/advisor/models/capabilities')
export const saveModel = (body: Partial<Model> & { vendor: string; modelKey: string }, id?: string) =>
  id ? api.put<Model>(`/advisor/models/${id}`, body) : api.post<Model>('/advisor/models', body)
export const deleteModel = (id: string) => api.del<void>(`/advisor/models/${id}`)
export const refreshModels = () =>
  api.post<{ providersChecked: number; verified: number; missing: number; problems: string[] }>(
    '/advisor/models/refresh')

export const getUsage = (days = 30) => api.get<Usage>(`/advisor/usage?days=${days}`)
export const getQuality = (days = 30) =>
  api.get<{ days: number; unboundVisuals: number; totalRuns: number; failedRuns: number }>(
    `/advisor/usage/quality?days=${days}`)
export const setBudget = (body: { capUsd?: number; hardStop?: boolean; alertAtPct?: number }) =>
  api.put<Budget>('/advisor/budget', body)

export const listSchedules = () => api.get<Schedule[]>('/advisor/schedules')
export const saveSchedule = (body: Partial<Schedule> & { playbookCode: string }, id?: string) =>
  id ? api.put<Schedule>(`/advisor/schedules/${id}`, body)
    : api.post<Schedule>('/advisor/schedules', body)
export const deleteSchedule = (id: string) => api.del<void>(`/advisor/schedules/${id}`)

export const listAssets = (page = 0, size = 30) =>
  api.get<Asset[]>(`/advisor/assets?page=${page}&size=${size}`)
export const generateAsset = (body: {
  kind: 'IMAGE' | 'VIDEO'
  prompt: string
  negativePrompt?: string
  aspectRatio?: string
  durationSeconds?: number
  runId?: string
  proposalId?: string
  mustNotDepict?: string[]
}) => api.post<Asset>('/advisor/assets', body)
export const assetDownload = (id: string) =>
  api.get<{ url: string; disclosure: string; model: string; prompt: string }>(
    `/advisor/assets/${id}/download`)
